import React, { ComponentProps, memo, ReactNode, useMemo } from 'react'

import { useOnClickOutside } from '@surglogs-app/hooks'

import { useToggleHandlers } from '../../hooks'

import { Box } from './Box'
import { Button } from './Button'
import { DropdownButtonMenu, MenuAlignment } from './DropdownButtonMenu'
import { DropdownIcon } from './DropdownIcon'

const DROPDOWN_MENU_PREFIX = 'dropdown-menu'

export interface DropdownButtonProps
  extends Pick<
      ComponentProps<typeof Button>,
      'size' | 'variant' | 'disabled' | 'icon' | 'addon' | 'shape'
    >,
    ComponentProps<typeof Box> {
  label?: ReactNode
  expanded?: boolean
  alignMenu?: MenuAlignment
  shouldHideMenuCallback?: Parameters<typeof useOnClickOutside>['2']
  dropdownButtonProps?: Partial<ComponentProps<typeof Button>>
  dropdownMenuProps?: Partial<ComponentProps<typeof DropdownButtonMenu>>
  /** Decides whether the menu should be rendered inside portal (for example in elements with overflow hidden), `false` by default */
  insidePortal?: boolean
  columnCount?: number
}

export const DropdownButton = memo(
  ({
    children,
    label,
    expanded = false,
    size = 'default',
    variant,
    disabled,
    icon,
    addon,
    shape,
    alignMenu = 'right',
    'aria-label': ariaLabel,
    shouldHideMenuCallback,
    dropdownButtonProps,
    dropdownMenuProps,
    insidePortal = false,
    columnCount,
    ...rest
  }: DropdownButtonProps) => {
    const {
      id,
      toggle,
      close,
      ref: buttonRef,
      isExpanded,
    } = useToggleHandlers({
      prefix: DROPDOWN_MENU_PREFIX,
      defaultValue: expanded,
    })

    const addonContent = useMemo(() => {
      return (
        <>
          {addon ? addon : null}
          <DropdownIcon
            {...{ isExpanded, w: 'auto', h: 'auto', isDisabled: disabled }}
          />
        </>
      )
    }, [addon, disabled, isExpanded])

    useOnClickOutside(buttonRef, close, shouldHideMenuCallback, 'click')

    return (
      <Box {...{ position: 'relative', ...rest }}>
        <Button
          {...{
            size,
            variant,
            disabled,
            icon,
            shape,
            addon: addonContent,
            onClick: toggle,
            ref: buttonRef,
            'aria-label': ariaLabel,
            'aria-expanded': isExpanded,
            'aria-haspopup': true,
            'aria-controls': id,
            gap: 'sm',
            ...dropdownButtonProps,
          }}>
          {label}
        </Button>
        {isExpanded ? (
          <DropdownButtonMenu
            {...{
              children,
              onClose: close,
              alignMenu,
              size,
              'aria-label': ariaLabel ? `${ariaLabel} dropdown` : 'dropdown',
              id,
              insidePortal,
              buttonRef,
              columnCount,
              ...dropdownMenuProps,
            }}
          />
        ) : null}
      </Box>
    )
  },
)
